import { useMemo, useState } from 'react'
import { Alert, Button, Empty, Input, Spin } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useAuthSession } from '@/lib/session'
import { useWorkflowJobs } from '../../hooks'
import type { CapacitySettings } from '../../types'
import { JobsListView } from './JobsListView'
import './WorkflowTab.css'

interface Props {
  settings: CapacitySettings
}

export function WorkflowTab({ settings }: Props) {
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const [staffFilter, setStaffFilter] = useState('all')
  const [stateFilter, setStateFilter] = useState('all')
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  function handleSearch() {
    setSearch(inputValue)
    setPage(1)
  }
  const pageSize = 25

  const { data, isLoading, isError, refetch } = useWorkflowJobs(clientId, {
    staffId: staffFilter === 'all' ? undefined : staffFilter,
    state: stateFilter === 'all' ? undefined : stateFilter,
    search: search.trim() || undefined,
    page,
    pageSize,
  })

  const jobs = useMemo(() => data?.items ?? [], [data?.items])
  const totalJobs = data?.totalCount ?? jobs.length
  const totalPages = Math.max(1, Math.ceil(totalJobs / pageSize))

  const stateOptions = useMemo(() => {
    return ['all', ...(data?.stateOptions ?? [])]
  }, [data?.stateOptions])
  const staffOptions = data?.staffOptions ?? []

  const summary = useMemo(() => {
    return jobs.reduce(
      (current, job) => ({
        budgetHours: current.budgetHours + (job.budgetHours ?? 0),
        usedHours: current.usedHours + job.usedHours,
        budgetAmount: current.budgetAmount + (job.budgetAmount ?? 0),
        usedAmount: current.usedAmount + job.usedAmount,
      }),
      { budgetHours: 0, usedHours: 0, budgetAmount: 0, usedAmount: 0 }
    )
  }, [jobs])
  const dollarMode = settings.budgetMode === 'dollars'
  const budgetSummary = dollarMode ? formatCurrency(summary.budgetAmount) : `${formatSummaryNumber(summary.budgetHours)} hrs`
  const usedSummary = dollarMode ? formatCurrency(summary.usedAmount) : `${formatSummaryNumber(summary.usedHours)} hrs`

  return (
    <div className="capacity-wireframe workflow-wireframe">
      <div className="filter-bar">
        <span className="filter-bar-label">Staff</span>
        <select
          className="filter-select"
          value={staffFilter}
          onChange={(event) => {
            setStaffFilter(event.target.value)
            setPage(1)
          }}
        >
          <option value="all">All Staff</option>
          {staffOptions.map((staff) => (
            <option key={staff.staffId} value={staff.staffId}>{staff.staffName}</option>
          ))}
        </select>
        <div className="filter-divider" />
        <span className="filter-bar-label">State</span>
        <select
          className="filter-select"
          value={stateFilter}
          onChange={(event) => {
            setStateFilter(event.target.value)
            setPage(1)
          }}
        >
          {stateOptions.map((state) => (
            <option key={state} value={state}>{state === 'all' ? 'All States' : state}</option>
          ))}
        </select>
        <div className="filter-divider" />
        <Input
          className="workflow-search"
          placeholder="Search jobs..."
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onPressEnter={handleSearch}
          suffix={<SearchOutlined className="cursor-pointer text-brand-500" onClick={handleSearch} />}
          allowClear
          style={{ width: 220 }}
        />
      </div>

      <main className="capacity-main">
        <div className="section">
          <div className="section-header">
            <span className="section-title">Active Jobs Workflow</span>
            <div className="section-actions">
              <span className="section-meta">
                {totalJobs} jobs | <strong>{budgetSummary}</strong> budget | <strong>{usedSummary}</strong> used
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="section-loading-inline">
              <Spin size="large" />
            </div>
          ) : isError ? (
            <div className="section-empty">
              <Alert
                type="error"
                message="Failed to load workflow jobs"
                action={<Button size="small" onClick={() => refetch()}>Retry</Button>}
              />
            </div>
          ) : jobs.length === 0 ? (
            <div className="workflow-empty">
              <Empty description="No jobs match the current filters" />
            </div>
          ) : (
            <>
              <JobsListView jobs={jobs} budgetMode={settings.budgetMode} />
              <div className="section-actions capacity-pagination">
                <span className="section-meta">Page {page} of {totalPages}</span>
                <div className="filter-divider" />
                <button type="button" className="btn btn-sm" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Prev</button>
                <button type="button" className="btn btn-sm" disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}>Next</button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}

function formatSummaryNumber(value: number) {
  return Number.isInteger(value) ? `${value}` : value.toFixed(1)
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(value || 0)
}
